import type { AudioCaptureOptions } from 'livekit-client'
import {
  MeetingTelemetryEvent,
  errorName,
  hashId,
  sanitizeErrorMessage,
} from '../../util/telemetry/meetingTelemetry'

/**
 * The slice of `LocalAudioTrack` the recovery needs. Narrow so tests can fake
 * it without a LiveKit room.
 */
export type RecoverableMicrophoneTrack = {
  constraints: MediaTrackConstraints
  mediaStreamTrack: { getSettings: () => MediaTrackSettings } | undefined
  restartTrack: (options?: AudioCaptureOptions) => Promise<void>
}

export type MicrophoneRecoveryPort = {
  setMicrophoneEnabled: (enabled: boolean) => Promise<unknown>
  getMicrophoneTrack: () => RecoverableMicrophoneTrack | undefined
  /** The room's audio capture defaults with any `deviceId` stripped. */
  fallbackCaptureOptions: () => AudioCaptureOptions
  /** Room and track state logged with an unmute failure. */
  failureContext: () => Record<string, unknown>
  logEvent: (name: string, params?: Record<string, unknown>) => void
  showAudioTrackMissingDialog: (reason: 'device_fallback_failed') => void
}

/**
 * The errors that mean "the pinned device id is no longer valid", not "the
 * user has no mic". LiveKit pins `{ exact: id }` on `switchActiveDevice`, and
 * Safari rejects a stale exact id on the next restart with
 * `OverconstrainedError`. `NotFoundError` is what Chrome/Firefox say for the
 * same situation.
 */
const DEVICE_CONSTRAINT_ERRORS = new Set([
  'OverconstrainedError',
  'NotFoundError',
])

/**
 * Safari on macOS reports a vanished CoreAudio device as `NotAllowedError`:
 * WebKit's `CoreAudioCaptureSource::create` returns
 * `MediaAccessDenialReason::PermissionDenied` with this message when the
 * device id no longer resolves. A real permission denial carries a different
 * message, so only this one is treated as a stale device.
 */
const WEBKIT_MISSING_DEVICE_MESSAGE = 'No CoreAudioCaptureSource device'

export function isDeviceConstraintError(error: unknown): boolean {
  const name = errorName(error)
  if (name === undefined) return false
  if (DEVICE_CONSTRAINT_ERRORS.has(name)) return true
  return (
    name === 'NotAllowedError' &&
    sanitizeErrorMessage(error) === WEBKIT_MISSING_DEVICE_MESSAGE
  )
}

/** Reduce a `ConstrainDOMString` to the id it names, for telemetry. */
export function unwrapDeviceId(
  constraint: ConstrainDOMString | undefined
): string | undefined {
  if (constraint === undefined) return undefined
  if (typeof constraint === 'string') return constraint
  if (Array.isArray(constraint)) return constraint[0]
  const exact = constraint.exact
  if (typeof exact === 'string') return exact
  if (Array.isArray(exact)) return exact[0]
  const ideal = constraint.ideal
  if (typeof ideal === 'string') return ideal
  if (Array.isArray(ideal)) return ideal[0]
  return undefined
}

/**
 * Unmute the microphone, falling back to the browser's default input when the
 * pinned device is gone.
 *
 * Never rejects. Every outcome is a telemetry event:
 * - clean unmute: nothing extra
 * - pinned device rejected, default worked: `local_audio_device_fallback`
 * - pinned device rejected, default failed too: `local_audio_device_fallback_failed`
 *   and the AudioTrackMissing dialog opens, since no mic works at all
 * - any other error: `meeting_unmute_failed`
 *
 * The fallback restarts the existing track with the room's capture defaults
 * minus `deviceId`. LiveKit turns that into `{ ideal: 'default' }`, a soft
 * constraint the browser satisfies with whatever mic it has, and on success
 * it replaces the track's stored stale constraint so later unmutes stop
 * retrying the dead id.
 */
export async function unmuteWithRecovery(
  port: MicrophoneRecoveryPort
): Promise<void> {
  try {
    await port.setMicrophoneEnabled(true)
    return
  } catch (firstError) {
    if (!isDeviceConstraintError(firstError)) {
      port.logEvent(MeetingTelemetryEvent.unmuteFailed, {
        error_name: errorName(firstError),
        error_message_sanitized: sanitizeErrorMessage(firstError),
        ...port.failureContext(),
      })
      return
    }

    const track = port.getMicrophoneTrack()
    const failedDeviceId = unwrapDeviceId(track?.constraints.deviceId)
    // `retry_no_track` changes no device, so its ids are always undefined.
    const fallbackPath = track ? 'restart' : 'retry_no_track'
    try {
      // With no published track, `setMicrophoneEnabled` goes through LiveKit's
      // `createLocalTracks`, which already retries `exact` as `ideal` on its
      // own, so there is nothing to restart: just try once more.
      if (track) await track.restartTrack(port.fallbackCaptureOptions())
      await port.setMicrophoneEnabled(true)
      port.logEvent(MeetingTelemetryEvent.localAudioDeviceFallback, {
        fallback_path: fallbackPath,
        error_name: errorName(firstError),
        failed_device_id_hash: hashId(failedDeviceId),
        device_id_hash: hashId(
          port.getMicrophoneTrack()?.mediaStreamTrack?.getSettings().deviceId
        ),
      })
    } catch (secondError) {
      port.logEvent(MeetingTelemetryEvent.localAudioDeviceFallbackFailed, {
        fallback_path: fallbackPath,
        first_error_name: errorName(firstError),
        failed_device_id_hash: hashId(failedDeviceId),
        error_name: errorName(secondError),
        error_message_sanitized: sanitizeErrorMessage(secondError),
      })
      port.showAudioTrackMissingDialog('device_fallback_failed')
    }
  }
}

export type AudioInputReselectPort = {
  /** The id LiveKit believes is the active mic, or undefined if none. */
  activeAudioInputId: () => string | undefined
  /**
   * LiveKit's `room.switchActiveDevice('audioinput', id, exact)`. Passing
   * `exact = false` stores `{ ideal: id }`, which the browser satisfies with
   * whatever mic it has if the id turns out to be stale by restart time.
   */
  switchAudioInput: (deviceId: string, exact: boolean) => Promise<boolean>
  roomState: () => string
  logEvent: (name: string, params?: Record<string, unknown>) => void
}

/**
 * After a `devicechange`, move the mic to the first remaining input when the
 * active one has vanished. Never rejects.
 *
 * The switch is soft (`exact = false`). Safari's enumerated list right after
 * an unplug can still carry ids that `getUserMedia` will reject with
 * `OverconstrainedError`, and LiveKit's default hard pin is what turns that
 * into a track stuck in the `ended` state. Whatever the soft switch still
 * gets wrong, {@link unmuteWithRecovery} corrects on the next unmute.
 */
export async function reselectAudioInputAfterDeviceChange(
  port: AudioInputReselectPort,
  audioInputDevices: Pick<MediaDeviceInfo, 'deviceId'>[]
): Promise<void> {
  const active = port.activeAudioInputId()
  if (!active) return
  if (audioInputDevices.length === 0) {
    port.logEvent('no_audio_input_devices')
    return
  }
  if (audioInputDevices.some((d) => d.deviceId === active)) return

  const next = audioInputDevices[0].deviceId
  port.logEvent(MeetingTelemetryEvent.audioInputSwitched, {
    reason: 'device_vanished',
    from_device_id_hash: hashId(active),
    to_device_id_hash: hashId(next),
    remaining_audio_inputs: audioInputDevices.length,
    room_state: port.roomState(),
  })
  try {
    await port.switchAudioInput(next, false)
  } catch (e) {
    port.logEvent(MeetingTelemetryEvent.mediaDeviceSwitchFailed, {
      kind: 'audioinput',
      trigger: 'devicechange',
      device_id_hash: hashId(next),
      error_name: errorName(e),
      error_message_sanitized: sanitizeErrorMessage(e),
    })
  }
}
